import { Injectable } from "@angular/core";
import { HttpCemex, CmxHttpOptions } from "@cemex-core/angular-services-v8";
import { EndpointService } from "./../../services/helpers/endpoint.service";
import { Observable, of, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { Bank } from "./../models/bank";
import { BankRequest } from "./../models/bank-request";
import { PaymentMethodDetailRequest } from "./../models/payment-method-detail-request";
import { PaymentNetworkDetail } from "./../models/payment-network-detail";
import { payments } from "../../../rollout-containers";

@Injectable()
export class BankService {
  private new_logo_banamex: boolean = false;

  constructor(protected http: HttpCemex, protected endpointService: EndpointService) {
    this.new_logo_banamex = payments.new_logo_banamex.isEnabled();
  }

  getBankList(request: BankRequest): Observable<Bank[]> {
    const endpoint = this.endpointService.getUrl("v1", "pe", "paymentsbanklist");

    const options = {
      params: {
          salesOrg: request.companyCode ?? "",
          land: request.countryCode ?? "",
          customerCode: request.customerCode ?? "",
      },
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      map((response: any) => {
        const banks: Bank[] = [];
        const disabledBanks = window["DISABLED_BANKS"].trim().split(",");
        const tempBanks = response[0].banks;

        tempBanks.forEach((bank) => {
          if (!disabledBanks.includes(bank.shortID4HouseBank)) {
            bank.imageSrc = this.getBankImageSrc(bank.shortID4HouseBank);
            banks.push(bank);
          }
        });

        return banks;
      }),
      catchError((error: any) => throwError("GET Request from Server error"))
    );
  }

  getBankListV2(request: BankRequest): Observable<Bank[]> {
    const endpoint = this.endpointService.getUrl("v2", "pe", "paymentsbanklist");

    const options = {
      params: {
          salesOrg: request?.companyCode ?? "",
          countryCode: request?.countryCode ?? "",
          customerCode: request?.customerCode ?? "",
      },
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      map((response: any) => {
        const banks: Bank[] = [];
        const disabledBanks = window["DISABLED_BANKS"].trim().split(",");
        const tempBanks = response.banks;

        tempBanks.forEach((bank) => {
          if (!disabledBanks.includes(bank.bankCode)) {
            bank.imageSrc = this.getBankImageSrc(bank.bankCode);
            banks.push(bank);
          }
        });

        return banks;
      }),
      catchError((error: any) => throwError("GET Request from Server error"))
    );
  }

  getPaymentMethodDetail(request: PaymentMethodDetailRequest): Observable<PaymentNetworkDetail[]> {
    const endpoint = this.endpointService.getUrl("v2", "pe", "paymentmethoddetail");
    const options = {
      params: {
          countryCode: request?.countryCode ?? "",
          bankCode: request?.bankCode ?? "",
          transactionTypeCode: request?.shortPaymentMethodCode ?? "",
      },
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      map((response: any) => {
        const details: PaymentNetworkDetail[] = [];

        if (response.length > 0) {
          response[0].detail.forEach((detail) => {
            details.push({
              codeDetail: detail.codeDetail,
              codeDetailDescription: detail.codeDetailDescription,
              imageSrc: this.getBankImageSrc(detail.codeDetail),
            });
          });
        }

        return details;
      }),
      catchError((error: any) => throwError("GET Request from Server error"))
    );
  }

  private getBankImageSrc(bankCode: string): string {
    switch (bankCode.trim()) {
      case "HSB01":
        return "cmx-hsbc.svg";
      case "BMR01":
        return "cmx-bbva.svg";
      case "BMX01":
        return this.new_logo_banamex ? "cmx-banamex.svg" : "cmx-citibanamex.svg";
      case "BNT01":
        return "cmx-banorte.png";
      case "STD01":
        return "cmx-santander.svg";
      case "JPM":
        return "jpmorgan-logo.svg";
      case "BDO01":
        return "cmx-bdo.svg";
      case "BHD01":
        return "logo-bhdleon.png";
      case "PSE01":
        return "pse-logo.svg";
      case "M":
        return "mastercard.svg";
      case "V":
        return "visa.svg";
    }
  }
}
